import { PhoneFormShape } from '@liveauctioneers/caterwaul-components/lib/Forms/PhoneForm/PhoneForm';
import {
    getE164FormattedNumber,
    getNationalPhoneNumberForm,
} from '@liveauctioneers/caterwaul-components/lib/Forms/PhoneForm/utils/phoneForm';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Modal } from '@liveauctioneers/hammer-ui-core/modal';
import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import ms from 'ms';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { DownloadMobilePromo } from '@/components/Modals/DownloadMobilePromo';
import { appDownloadPromoComposableModalClassName } from '@/components/Modals/DownloadMobilePromo/constants';
import { modalTypes } from '@/enums';
import { useAppDownloadPromptSuppression } from '@/hooks/useAppDownloadPromptSuppression';
import { AlertTypes, DeliveryType } from '@/redux/api/alerts';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isCompleteAccountModalOpen } from '@/redux/modules/account/shouldLogin/shouldLogin.selectors';
import { updateAccountDetails } from '@/redux/modules/account/updateAccount/updateAccount.actions';
import {
    getBidderCountryAllowedToReceiveSMS,
    getBidderEmail,
    getBidderPhoneNumber,
    getDefaultAddressPostalCode,
    getUserFirstName,
    getUserLastName,
} from '@/redux/modules/account/user/user.selectors';
import { getCookie, setCookie } from '@/redux/modules/cookies';
import { getSavedItemsCount } from '@/redux/modules/item/saved/savedItems.selectors';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import { getSubscribedToSMSAlerts, toggleSmsAlert } from '@/redux/modules/smsAlerts';
import {
    AppDownloadPromptTrigger,
    TrackEnableSmsOnSavePromptActionTaken,
    useTrackAppDownloadPromptViewed,
    useTrackEnableSmsOnSavePrompt,
    useTrackSmsNotificationToggle,
} from '@/utils/amplitude/analytics/notifications';
import { TrackSmsNotificationToggleEventTrigger } from '@/utils/amplitude/analytics/notifications/trackSmsNotificationToggle.analytics';
import { useAppDownloadPromptEligibility } from '@/utils/amplitude/experiments/hooks/useAppDownloadPromptEligibility';
import { canShowAppDownloadPromoOnPath, isNotificationsPage } from '@/utils/urls';
import { AppDownloadSource } from '@/utils/urls/appDownloadRedirectUrl';

import { EnableSmsOnSaveModal } from './components/EnableSmsOnSaveModal/EnableSmsOnSaveModal';
import { EnableSmsOnSavePopover } from './components/EnableSmsOnSavePopover/EnableSmsOnSavePopover';
const COOKIE_MAX_AGE = ms('60 days') / 1000; // 60 days in seconds
const COOKIE_NAME = 'dismissableEnableSmsOnSavePrompt';
const COOKIE_BODY = 'dismissed';
const SMS_CLOSE_TRANSITION_MS = 300;
const APP_DOWNLOAD_DELAY_BUFFER_MS = 50;
const APP_DOWNLOAD_AFTER_SMS_DELAY_MS = SMS_CLOSE_TRANSITION_MS + APP_DOWNLOAD_DELAY_BUFFER_MS;
const APP_DOWNLOAD_SAVE_TRIGGER_DELAY_MS = 800;

export type EnableSmsOnSaveForm = {
    isFormSubmitAttempted: boolean;
    isSubmitLoading: boolean;
    isValid: boolean;
    phoneData: PhoneFormShape;
};

type SmsCompletionReason = 'accepted' | 'dismiss' | 'outside_click' | 'reject';

// The main wrapper component that conditionally renders a modal or a popover
export const EnableSmsOnSavePrompt = () => {
    const { formatMessage } = useIntl();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const isAppDownloadEligible = useAppDownloadPromptEligibility();
    const { canShowAppDownloadPrompt, dismissAppDownloadPrompt } = useAppDownloadPromptSuppression();
    const isMobile = useWindowIsMobile();
    const isOnNotificationsPage = isNotificationsPage(location.pathname);

    const firstName = useAppSelector(getUserFirstName);
    const lastName = useAppSelector(getUserLastName);
    const email = useAppSelector(getBidderEmail);
    const postalCode = useAppSelector(getDefaultAddressPostalCode);

    const openModal = useAppSelector(getOpenModal);

    const hasDismissedCookie = useAppSelector((state) => getCookie(state, COOKIE_NAME)) === COOKIE_BODY;
    const completeAccountModalOpen = useAppSelector(isCompleteAccountModalOpen);
    const hasSmsAlertsEnabled = useAppSelector(getSubscribedToSMSAlerts);
    const smsAllowed = useAppSelector(getBidderCountryAllowedToReceiveSMS);

    const bidderPhoneNumber = useAppSelector(getBidderPhoneNumber);
    const savedItemCount = useAppSelector((state) => getSavedItemsCount(state));
    const prevSavedItemCount = usePrevious(savedItemCount);

    const [hasScrolled, setHasScrolled] = useState(false);

    const [showWidget, setShowWidget] = useState(false);
    const [showAppDownloadPromo, setShowAppDownloadPromo] = useState(false);
    const [renderTransition, setRenderTransition] = useState(false);
    const [shouldAttemptDownloadAfterSms, setShouldAttemptDownloadAfterSms] = useState(false);
    const initialPhoneForm = getNationalPhoneNumberForm(bidderPhoneNumber || '');

    // AMPLITUDE TRACKING
    const trackEnableSmsOnSavePrompt = useTrackEnableSmsOnSavePrompt();
    const trackAppDownloadPromptViewed = useTrackAppDownloadPromptViewed();
    const trackSmsNotificationToggle = useTrackSmsNotificationToggle();
    const hasTrackedAppDownloadPromptViewed = useRef(false);

    const methods = useForm<EnableSmsOnSaveForm>({
        defaultValues: {
            isFormSubmitAttempted: false,
            isSubmitLoading: false,
            isValid: Boolean(getE164FormattedNumber(initialPhoneForm)),
            phoneData: initialPhoneForm,
        },
    });

    const {
        formState: { dirtyFields, errors },
        handleSubmit,
        reset,
        setValue,
    } = methods;

    // For the first time the widget is shown, we need to set the phone number
    // in the form if there is a delay in fetching the phone number from Redux state
    useEffect(() => {
        if (showWidget && bidderPhoneNumber && !dirtyFields.phoneData) {
            const form = getNationalPhoneNumberForm(bidderPhoneNumber);
            setValue('phoneData', form, { shouldDirty: false, shouldValidate: false });
            setValue('isValid', Boolean(getE164FormattedNumber(form)), { shouldDirty: false, shouldValidate: false });
        }
    }, [bidderPhoneNumber, dirtyFields.phoneData, setValue, showWidget]);

    // Reset the form when the widget opens (or closes),
    // so each open re-derives phoneData from bidderPhoneNumber
    useEffect(() => {
        if (showWidget) {
            reset(
                {
                    isFormSubmitAttempted: false,
                    isSubmitLoading: false,
                    isValid: Boolean(getE164FormattedNumber(getNationalPhoneNumberForm(bidderPhoneNumber || ''))),
                    phoneData: getNationalPhoneNumberForm(bidderPhoneNumber || ''),
                },
                { keepDirty: false, keepErrors: false, keepTouched: false }
            );
        }
    }, [showWidget, bidderPhoneNumber, reset]);

    const useModal = isMobile || hasScrolled;

    const handleSetCookie = useCallback((): void => {
        dispatch(
            setCookie({
                maxAge: COOKIE_MAX_AGE,
                name: COOKIE_NAME,
                value: COOKIE_BODY,
            })
        );
    }, [dispatch]);

    const onCloseSaveWidget = useCallback(
        (isModal?: boolean) => {
            setRenderTransition(false);
            if (isModal) {
                setShowWidget(false);
            }
            // Wait for close transition to complete before setting cookie
            setTimeout(() => {
                handleSetCookie();
            }, SMS_CLOSE_TRANSITION_MS);
        },
        [handleSetCookie]
    );

    const tryShowAppDownloadPromo = useCallback(() => {
        if (!canShowAppDownloadPromoOnPath(location.pathname)) {
            return;
        }

        if (
            isAppDownloadEligible &&
            canShowAppDownloadPrompt &&
            !completeAccountModalOpen &&
            openModal === modalTypes.NONE
        ) {
            setShowAppDownloadPromo(true);
            return;
        }
    }, [canShowAppDownloadPrompt, completeAccountModalOpen, isAppDownloadEligible, location.pathname, openModal]);

    const completeSmsFlow = useCallback(
        (_reason: SmsCompletionReason, isModal?: boolean) => {
            if (shouldAttemptDownloadAfterSms) {
                // Delay app download prompt until after SMS close transition plus a small buffer.
                window.setTimeout(() => {
                    tryShowAppDownloadPromo();
                }, APP_DOWNLOAD_AFTER_SMS_DELAY_MS);
                setShouldAttemptDownloadAfterSms(false);
            }
            onCloseSaveWidget(isModal);
        },
        [onCloseSaveWidget, shouldAttemptDownloadAfterSms, tryShowAppDownloadPromo]
    );

    const onTextMeClick = useCallback(
        async (data: EnableSmsOnSaveForm) => {
            setValue('isFormSubmitAttempted', true);

            const formattedPhoneNumber = getE164FormattedNumber(data.phoneData);
            const hasPhoneInputError = !formattedPhoneNumber || Boolean(errors.phoneData?.message) || !data.isValid;

            if (hasPhoneInputError) {
                return;
            }

            setValue('isSubmitLoading', true);
            await dispatch(
                updateAccountDetails({
                    email,
                    firstName,
                    lastName,
                    mobile: formattedPhoneNumber,
                    zipcode: postalCode,
                })
            );
            await dispatch(
                toggleSmsAlert(AlertTypes.All, DeliveryType.sms, true, true, () => {
                    //   Handle error state
                    trackEnableSmsOnSavePrompt({ actionTaken: TrackEnableSmsOnSavePromptActionTaken.Error });
                    setValue('isSubmitLoading', false);
                    return;
                })
            );

            trackEnableSmsOnSavePrompt({ actionTaken: TrackEnableSmsOnSavePromptActionTaken.Accepted });
            trackSmsNotificationToggle({
                auctionRemindersEnabled: true,
                eventTrigger: TrackSmsNotificationToggleEventTrigger.SavePrompt,
                outbidEnabled: true,
                paymentRemindersEnabled: true,
            });

            setValue('isSubmitLoading', false);
            completeSmsFlow('accepted', useModal);
            toast('success', {
                description: formatMessage({ id: 'enableSmsOnSave.toast.description' }),
                title: formatMessage({ id: 'enableSmsOnSave.toast.title' }),
            });
        },
        [
            setValue,
            errors.phoneData?.message,
            dispatch,
            email,
            firstName,
            lastName,
            postalCode,
            trackEnableSmsOnSavePrompt,
            trackSmsNotificationToggle,
            completeSmsFlow,
            formatMessage,
            useModal,
        ]
    );

    const onOutsideTooltipClick = useCallback(() => {
        trackEnableSmsOnSavePrompt({ actionTaken: TrackEnableSmsOnSavePromptActionTaken.Closed });
        completeSmsFlow('outside_click');
    }, [completeSmsFlow, trackEnableSmsOnSavePrompt]);

    useEffect(() => {
        window.requestAnimationFrame(() => {
            setRenderTransition(showWidget);
        });
    }, [showWidget]);

    useEffect(() => {
        const shouldTriggerWidget =
            smsAllowed &&
            prevSavedItemCount !== undefined &&
            savedItemCount > prevSavedItemCount &&
            !hasDismissedCookie &&
            openModal === modalTypes.NONE &&
            !hasSmsAlertsEnabled;

        if (shouldTriggerWidget) {
            setHasScrolled(Boolean(window.scrollY > 0));
            setShowWidget(true);
        }
    }, [prevSavedItemCount, savedItemCount, hasDismissedCookie, openModal, hasSmsAlertsEnabled, smsAllowed]);

    // LAAC-1674: post-saved app download — SMS flow takes precedence when it would have opened.
    // isAppDownloadEligible = Amplitude treatment (LAAC-1711); unrelated to incognito or dismiss cookies.
    useEffect(() => {
        const justSaved = prevSavedItemCount !== undefined && savedItemCount > prevSavedItemCount;
        if (!justSaved) {
            return;
        }

        const wouldShowSms = smsAllowed && !hasDismissedCookie && openModal === modalTypes.NONE && !hasSmsAlertsEnabled;

        if (wouldShowSms) {
            setShouldAttemptDownloadAfterSms(true);
            return;
        }

        setShouldAttemptDownloadAfterSms(false);
        window.setTimeout(() => {
            tryShowAppDownloadPromo();
        }, APP_DOWNLOAD_SAVE_TRIGGER_DELAY_MS);
    }, [
        hasDismissedCookie,
        hasSmsAlertsEnabled,
        location.pathname,
        openModal,
        prevSavedItemCount,
        savedItemCount,
        smsAllowed,
        tryShowAppDownloadPromo,
    ]);

    const canShowWidget =
        smsAllowed &&
        !hasDismissedCookie &&
        !completeAccountModalOpen &&
        !hasSmsAlertsEnabled &&
        !isOnNotificationsPage;

    useEffect(() => {
        if (canShowWidget && showWidget) {
            trackEnableSmsOnSavePrompt({ actionTaken: TrackEnableSmsOnSavePromptActionTaken.Viewed });
        }
    }, [canShowWidget, showWidget, trackEnableSmsOnSavePrompt]);

    useEffect(() => {
        if (!showAppDownloadPromo) {
            hasTrackedAppDownloadPromptViewed.current = false;
            return;
        }

        if (!hasTrackedAppDownloadPromptViewed.current) {
            trackAppDownloadPromptViewed({ trigger: AppDownloadPromptTrigger.Save });
            hasTrackedAppDownloadPromptViewed.current = true;
        }
    }, [showAppDownloadPromo, trackAppDownloadPromptViewed]);

    const onCloseAppDownloadPromo = useCallback(() => {
        dismissAppDownloadPrompt();
        setShowAppDownloadPromo(false);
    }, [dismissAppDownloadPrompt]);

    return (
        <>
            <FormProvider {...methods}>
                {canShowWidget && (
                    <form
                        data-testid="enableSmsOnSaveForm"
                        id="enableSmsOnSaveForm"
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                handleSubmit(onTextMeClick)();
                            }
                        }}
                        onSubmit={handleSubmit(onTextMeClick)}
                    >
                        {useModal && (
                            <EnableSmsOnSaveModal
                                onCloseSaveWidget={completeSmsFlow}
                                setShow={setShowWidget}
                                show={showWidget}
                            />
                        )}
                        {!useModal && showWidget && (
                            <EnableSmsOnSavePopover
                                onCloseSaveWidget={() => completeSmsFlow('reject')}
                                onOutsideTooltipClick={onOutsideTooltipClick}
                                renderTransition={renderTransition}
                                setShowWidget={setShowWidget}
                                showWidget={showWidget}
                            />
                        )}
                    </form>
                )}
            </FormProvider>
            <Modal
                className={appDownloadPromoComposableModalClassName}
                closeModalOnBackdropClick
                handleClose={onCloseAppDownloadPromo}
                preventScrollWhileOpen
                show={showAppDownloadPromo}
                type="composable"
            >
                <DownloadMobilePromo
                    device={isMobile ? 'mobile' : 'desktop'}
                    onClose={onCloseAppDownloadPromo}
                    onGetApp={onCloseAppDownloadPromo}
                    source={AppDownloadSource.SavedItem}
                />
            </Modal>
        </>
    );
};
